import type { ConversationId, MessageId } from '@cyphex/types';

/** Centralised TanStack Query keys. Never inline a key array. */
export const CHAT_KEYS = {
  all: ['chat'] as const,
  conversations: (filter: string, query: string) => ['chat', 'conversations', filter, query] as const,
  conversation: (id: ConversationId) => ['chat', 'conversation', id] as const,
  timeline: (id: ConversationId) => ['chat', 'timeline', id] as const,
  /** The FULL roster. `conversation.members` is capped at eight by the API. */
  members: (id: ConversationId) => ['chat', 'members', id] as const,
  /** Permalink window: a page centred on one message rather than the tail. */
  timelineAround: (id: ConversationId, messageId: MessageId) =>
    ['chat', 'timeline', id, 'around', messageId] as const,
  thread: (id: ConversationId, root: MessageId) => ['chat', 'timeline', id, 'thread', root] as const,
  scheduled: (id: ConversationId) => ['chat', 'scheduled', id] as const,
  people: (query: string) => ['chat', 'people', query] as const,
  gifs: (query: string) => ['chat', 'gifs', query] as const,
  saved: () => ['chat', 'saved'] as const,
  reminders: () => ['chat', 'reminders'] as const,
  preferences: () => ['chat', 'preferences'] as const,
  selfConversation: () => ['chat', 'conversation', 'self'] as const,
} as const;

/* ── Paging ───────────────────────────────────────────────────────────────── */
/** Keyset page size for the timeline. Small enough to render in one frame. */
export const TIMELINE_PAGE_SIZE = 40;
export const CONVERSATION_PAGE_SIZE = 40;

/* ── Virtualisation ───────────────────────────────────────────────────────── */
export const TIMELINE_OVERSCAN = 8;
export const TIMELINE_ESTIMATED_ROW_PX = 64;
export const LIST_OVERSCAN = 6;
/** Compact single-line chat row: 32px avatar + name/preview lines + padding. */
export const LIST_ROW_PX = 52;
/** Distance from the top that triggers loading the previous page. */
export const LOAD_MORE_THRESHOLD_PX = 320;
/** Distance from the bottom that still counts as "pinned to live". */
export const STICK_TO_BOTTOM_PX = 96;

/* ── Realtime pacing ──────────────────────────────────────────────────────── */
/** Never emit a typing frame more often than this while the user types. */
export const TYPING_THROTTLE_MS = 3_000;
/** Local expiry for a remote typing indicator when no refresh arrives. */
export const TYPING_TTL_MS = 6_000;
/** Read receipts are coalesced into one frame per window. */
export const READ_FLUSH_MS = 700;
/** Presence deltas are batched before touching React state. */
export const PRESENCE_COALESCE_MS = 250;
/** Heartbeat to keep the presence TTL alive (server TTL is 45s). */
export const SOCKET_HEARTBEAT_MS = 20_000;
export const SOCKET_BACKOFF_MIN_MS = 500;
export const SOCKET_BACKOFF_MAX_MS = 15_000;

/* ── Composition ──────────────────────────────────────────────────────────── */
/**
 * Consecutive messages from one sender inside this window are grouped — one
 * avatar + name + time for the whole run, bare bubbles after it.
 *
 * Teams groups a run generously: a burst of four short lines typed over ten
 * minutes still reads as ONE turn in the conversation, not four. A five-minute
 * window broke those bursts apart and put a bold name header above almost every
 * bubble, which is exactly the "wall of names" this widens away from.
 */
export const GROUPING_WINDOW_MS = 10 * 60_000;
export const DRAFT_SAVE_DEBOUNCE_MS = 400;
export const SEARCH_DEBOUNCE_MS = 200;
export const MAX_ATTACHMENTS = 10;
/** The API's own limit (20 GiB), not a second opinion about it. */
export { MAX_UPLOAD_BYTES } from '@cyphex/types';
export const MAX_VOICE_MS = 5 * 60_000;
export const WAVEFORM_BUCKETS = 64;

/* ── Outbox ───────────────────────────────────────────────────────────────── */
export const OUTBOX_MAX_ATTEMPTS = 8;
export const OUTBOX_BACKOFF_BASE_MS = 800;

/** Query-param that turns a chat route into a bare pop-out window. */
export const POPOUT_PARAM = 'popout';
export const POPOUT_CHANNEL = 'cyphex-chat';

/* ── Permalinks ───────────────────────────────────────────────────────────── */
/** `?m=<messageId>` — the deep link "Copy link to message" produces. */
export const MESSAGE_PARAM = 'm';
/** How long the linked message stays highlighted after the jump. */
export const HIGHLIGHT_MS = 2_600;

/* ── Quick reactions ──────────────────────────────────────────────────────── */
/** Shown on hover until the user picks their own three in Settings. */
export const DEFAULT_QUICK_REACTIONS = ['👍', '🎉', '❤️'] as const;
export const MAX_QUICK_REACTIONS = 3;
